import { ConfigProvider, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TeamMemberVolumeStat } from '@/api/type-response'
import InfoIcon from '@/assets/icons/info-i.svg?react'
import { Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { formatBytesToGB } from '@/utils/formatter'

interface DataTableProps {
  members: TeamMemberVolumeStat[]
}

/**
 * Data management table component for admin view
 * Displays member storage information without checkbox selection
 */
export function DataTable({ members }: DataTableProps) {
  const { t } = useTranslation()

  const columns: ColumnsType<TeamMemberVolumeStat> = [
    // ID column
    {
      title: <Trans i18nKey="instance.tableHeaderID">ID</Trans>,
      key: 'resID',
      width: 150,
      className: 't-sm',
      fixed: 'left',
      render: (_: unknown, record: TeamMemberVolumeStat) => record.user.resID,
    },

    // Owner column
    {
      title: <Trans i18nKey="team.admin.table.owner">Owner</Trans>,
      key: 'owner',
      width: 200,
      className: 't-sm',
      render: (_: unknown, record: TeamMemberVolumeStat) => {
        return record.user.user.nickname || record.user.user.name
      },
    },

    // Usage column
    {
      title: <Trans i18nKey="teamData.admin.usage">Usage</Trans>,
      key: 'usage',
      width: 200,
      className: 't-sm',
      render: (_: unknown, record: TeamMemberVolumeStat) => {
        // Only fsSubPart is populated when using only_fs API parameter
        const usageGB = formatBytesToGB(record.fsSubPart.usageBytes || 0)
        const quotaGB = record.fsSubPart.quotaGB || 0

        return (
          <span>
            {usageGB}
            <span className="text-tertiary"> / {quotaGB} GB</span>
          </span>
        )
      },
    },

    // Regions column
    {
      title: (
        <div className="flex items-center gap-1">
          <Trans i18nKey="teamData.admin.regions">Regions</Trans>
        </div>
      ),
      key: 'regions',
      width: 120,
      className: 't-sm',
      fixed: 'right',
      render: (_: unknown, record: TeamMemberVolumeStat) => {
        // Only fsSubPart is populated when using only_fs API parameter
        const regions = record.fsSubPart.regions || 0

        return (
          <div className="flex items-center gap-2">
            <span>{regions}</span>
            <Tooltip title={t('teamData.admin.regionsTooltip', 'Number of regions')}>
              <InfoIcon className="w-4 h-4 text-tertiary cursor-help" />
            </Tooltip>
          </div>
        )
      },
    },

    // Action column
    {
      title: <Trans i18nKey="instance.tableHeaderAction">Action</Trans>,
      key: 'action',
      width: 120,
      className: 't-sm !text-right',
      fixed: 'right' as const,
      render: (_: unknown, record: TeamMemberVolumeStat) => {
        const memberName = record.user.user.nickname || record.user.user.name
        return (
          <div className="flex gap-3 items-center justify-end">
            <Link
              to={`/team/data/member/${record.user.resID}`}
              state={{ memberName, ownerName: memberName }}
              className="text-btn-secondary t-sb cursor-pointer"
            >
              <Trans i18nKey="team.admin.table.details">Details</Trans>
            </Link>
          </div>
        )
      },
    },
  ]

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'rgb(var(--color-panel-white))',
            headerColor: 'rgb(var(--color-text-secondary))',
            rowHoverBg: 'rgb(var(--color-table-row-selected))',
          },
        },
      }}
    >
      <Table
        rowKey={(record) => record.user.resID}
        columns={columns}
        dataSource={members}
        pagination={false}
        scroll={{ x: 'max-content' }}
        className="data-table data-table-admin"
        locale={{
          emptyText: (
            <EmptyView
              title={
                <span className="t-sm text-secondary">
                  <Trans i18nKey="common.emptyViewNoDataMsg" />
                </span>
              }
            />
          ),
        }}
      />
    </ConfigProvider>
  )
}
